import { Box, Button, CircularProgress, Typography } from '@mui/material'
import type { Ref } from 'react'

import {
  BORDER,
  ERROR_TEXT,
  MUTED_TEXT,
  NAVY,
  WARNING_BG,
  WARNING_BORDER,
  WARNING_TEXT,
  searchButtonSx,
  secondaryButtonSx,
} from './styles'
import { MAX_SELECTIONS } from '@/utils/databaseSearchSession'

export type SearchBarState = 'ready' | 'searched' | 'stale'

export interface SearchBarError {
  message: string
  /** Shown in bold so a rate limit stands out from a refusal. */
  rateLimited?: boolean
  /**
   * Counts attempts. A live region only speaks when its text changes, so the
   * same message twice in a row gets a trailing no-break space on odd counts.
   */
  attempt?: number
}

interface SearchBarProps {
  state: SearchBarState
  /** A refusal or rate-limit message from the last attempt. */
  error: SearchBarError | null
  searching: boolean
  onReset: () => void
  onSearch: () => void
  /** Opportunities ticked; the count and View & export show only above zero. */
  pickedCount: number
  onViewSelections: () => void
  /** Where focus goes when the control that had it disappears. */
  searchButtonRef?: Ref<HTMLButtonElement>
}

const STATUS: Record<SearchBarState, string> = {
  ready: 'Set your filters, then click Search.',
  searched: '',
  stale: 'Filters changed since this search. Click Search to update.',
}

/**
 * Heads the results: status on the left, Reset filters and Search on the right.
 * Sticks to the top of the results on wide screens. On narrow ones it is fixed
 * above the Help Scout beacon, which index.html lifts to 90px on phones; its
 * z-index stays under the open phone menu (100) so the menu covers it.
 */
export default function SearchBar({
  state,
  error,
  searching,
  onReset,
  onSearch,
  pickedCount,
  onViewSelections,
  searchButtonRef,
}: SearchBarProps) {
  const stale = state === 'stale'
  return (
    <Box
      data-testid="search-bar"
      data-state={state}
      sx={{
        position: { xs: 'fixed', md: 'sticky' },
        top: { md: 0 },
        bottom: { xs: '160px', sm: '110px', md: 'auto' },
        left: { xs: 0, md: 'auto' },
        right: { xs: 0, md: 'auto' },
        zIndex: { xs: 90, md: 1200 },
        display: 'flex',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        px: 2,
        py: 1.25,
        mb: { md: 1.5 },
        bgcolor: stale ? WARNING_BG : '#fff',
        border: '1px solid',
        borderColor: stale ? WARNING_BORDER : BORDER,
        borderRadius: { xs: 0, md: 2 },
        boxShadow: { xs: '0 -2px 8px rgba(0,0,0,0.08)', md: 'none' },
      }}
    >
      <Typography
        role="status"
        sx={{
          fontSize: 14,
          color: error ? ERROR_TEXT : stale ? WARNING_TEXT : MUTED_TEXT,
          fontWeight: error?.rateLimited ? 600 : 400,
        }}
      >
        {error ? `${error.message}${(error.attempt ?? 0) % 2 ? '\u00a0' : ''}` : STATUS[state]}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          width: { xs: '100%', md: 'auto' },
        }}
      >
        {pickedCount > 0 && (
          // Its own row above Reset filters and Search on narrow screens.
          <Box
            data-testid="search-bar-selections"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flex: { xs: '1 1 100%', md: 'none' },
              pr: { md: 1.25 },
              mr: { md: 0.25 },
              pb: { xs: 1, md: 0 },
              borderRight: { md: `1px solid ${BORDER}` },
              borderBottom: { xs: `1px solid ${BORDER}`, md: 'none' },
            }}
          >
            <Typography sx={{ fontSize: 14, color: NAVY, whiteSpace: 'nowrap' }}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {pickedCount} selected
              </Box>{' '}
              <Box component="span" sx={{ color: MUTED_TEXT }}>
                (max {MAX_SELECTIONS})
              </Box>
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disableRipple
              onClick={onViewSelections}
              sx={secondaryButtonSx}
            >
              View &amp; export
            </Button>
          </Box>
        )}
        <Button
          variant="outlined"
          size="small"
          disableRipple
          onClick={onReset}
          sx={secondaryButtonSx}
        >
          Reset filters
        </Button>
        <Button
          variant="contained"
          disableRipple
          ref={searchButtonRef}
          // aria-disabled, not disabled: a disabled button drops keyboard focus.
          aria-disabled={searching || undefined}
          onClick={() => {
            if (!searching) onSearch()
          }}
          startIcon={
            // Hidden: the page's status line announces "Searching…".
            searching ? <CircularProgress size={14} color="inherit" aria-hidden="true" /> : null
          }
          sx={{ ...searchButtonSx, ...(searching && { opacity: 0.7, cursor: 'progress' }) }}
        >
          Search
        </Button>
      </Box>
    </Box>
  )
}
